/**
 * One error type for every non-2xx response, normalised from the shapes the
 * backend actually produces:
 *
 *   ValidationPipe  → { statusCode, message: string[], error }
 *   HttpException   → { statusCode, message: string,   error }
 *   Prisma filter   → { statusCode, message: string,   error }
 */

export interface ApiErrorBody {
  statusCode?: number;
  message?: string | string[];
  error?: string;
}

export class ApiError extends Error {
  readonly status: number;
  /** Every message the server sent, un-collapsed. */
  readonly messages: string[];
  /**
   * Validation messages bucketed by the field they mention. class-validator
   * prefixes each message with the property name ("email must be a valid
   * email address"), which is enough to drive react-hook-form's setError.
   */
  readonly fieldErrors: Record<string, string>;

  constructor(status: number, messages: string[], fieldErrors: Record<string, string> = {}) {
    super(messages[0] ?? `Request failed with status ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.messages = messages;
    this.fieldErrors = fieldErrors;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isConflict(): boolean {
    return this.status === 409;
  }

  get isRateLimited(): boolean {
    return this.status === 429;
  }

  /** True for the "server is unreachable" case, where retrying may help. */
  get isNetworkError(): boolean {
    return this.status === 0;
  }

  static network(message = 'Cannot reach the server. Check your connection and try again.') {
    return new ApiError(0, [message]);
  }

  static async fromResponse(response: Response): Promise<ApiError> {
    let body: ApiErrorBody | null = null;

    try {
      body = (await response.clone().json()) as ApiErrorBody;
    } catch {
      // Non-JSON error body (a proxy's HTML 502 page, say) — fall through.
    }

    const messages = normaliseMessages(body, response);
    return new ApiError(response.status, messages, toFieldErrors(messages));
  }
}

function normaliseMessages(body: ApiErrorBody | null, response: Response): string[] {
  const raw = body?.message;

  if (Array.isArray(raw)) return raw.filter((m): m is string => typeof m === 'string' && m !== '');
  if (typeof raw === 'string' && raw !== '') return [raw];
  if (typeof body?.error === 'string' && body.error !== '') return [body.error];

  return [response.statusText || `Request failed with status ${response.status}`];
}

/**
 * "password must be at least 12 characters" → { password: "..." }.
 * First message per field wins; anything that does not start with a known-looking
 * property token is left out and surfaces as a form-level error instead.
 */
function toFieldErrors(messages: string[]): Record<string, string> {
  const fieldErrors: Record<string, string> = {};

  for (const message of messages) {
    const match = /^([a-z][a-zA-Z0-9_]*)\s/.exec(message);
    const field = match?.[1];
    if (field && !(field in fieldErrors)) {
      fieldErrors[field] = message;
    }
  }

  return fieldErrors;
}
